/* Qorvex algo products — one place that names them.
 *
 * Two products sit on top of the strategy engine, and they answer two
 * different questions a trader arrives with:
 *
 *   Atlas — "give me something that already works."  A curated marketplace of
 *           ready-made, execution-grade strategies. No building: pick one,
 *           check its published record, subscribe, deploy. Two tiers —
 *           ATLAS OPEN publishes its rules in full (whitebox), ATLAS EDGE is
 *           a sealed institutional model that publishes its behaviour but not
 *           its logic (blackbox).
 *
 *   Forge — "let me build my own."  A strategy-building suite with four lanes
 *           onto the same engine, so the choice of lane is about how you like
 *           to work rather than what you are allowed to run: Classic (no-code
 *           blocks), Copilot (describe it in a sentence), Pro (our desk builds
 *           it from your written rules), Python (write the logic yourself).
 *
 * Every lane lands on the SAME artefact — a strategy version the engine can
 * load — so backtest, paper and live are identical downstream regardless of
 * how the strategy was authored. That is the point of the suite: the build
 * lane is a preference, not a capability tier.
 *
 * Naming lives here and nowhere else. Renaming a product is one edit to this
 * file; nothing below it hardcodes a product name.
 */

export interface ProductBrand {
  /** Short name used in running text and tabs. */
  name: string
  /** Full name for page titles and marketing surfaces. */
  full: string
  /** One line that says what the product is for. */
  tagline: string
  /** Label this product's screen carries in the admin console. */
  adminLabel: string
}

export const PRODUCTS = {
  marketplace: {
    name: 'Atlas',
    full: 'Qorvex Atlas',
    tagline: 'Ready-made strategies, deployed in a few clicks.',
    adminLabel: 'Atlas Listings',
  },
  studio: {
    name: 'Forge',
    full: 'Qorvex Forge',
    tagline: 'Build a strategy your way — blocks, AI, our desk, or Python.',
    adminLabel: 'Forge Studio',
  },
} as const satisfies Record<string, ProductBrand>

/** Marketplace tiers. OPEN publishes its rules; EDGE publishes its record. */
export const ATLAS_TIERS = {
  OPEN: {
    label: 'Atlas Open',
    short: 'OPEN',
    blurb: 'Whitebox. Every rule, parameter and filter is published in full.',
  },
  EDGE: {
    label: 'Atlas Edge',
    short: 'EDGE',
    blurb:
      'Institutional model. The logic is sealed; the published record, risk ' +
      'profile and live behaviour are not.',
  },
} as const

export type AtlasTier = keyof typeof ATLAS_TIERS

/** The four Forge build lanes. `language` is what the engine ends up loading. */
export const FORGE_LANES = [
  {
    id: 'CLASSIC',
    label: 'Classic Build',
    kicker: 'No-code',
    language: 'RULE_GRAPH',
    blurb:
      'Assemble entry, exit and risk from structured blocks. The block graph ' +
      'is what the engine runs — there is no code generation step to drift.',
  },
  {
    id: 'COPILOT',
    label: 'Copilot',
    kicker: 'AI builder',
    language: 'RULE_GRAPH',
    blurb:
      'Describe the strategy in a sentence. Copilot drafts the block graph, ' +
      'you review every block it chose, and nothing is saved until you accept.',
  },
  {
    id: 'PRO',
    label: 'Pro Build',
    kicker: 'Built for you',
    language: 'RULE_GRAPH',
    blurb:
      'Send your rules in writing and our desk builds, backtests and hands ' +
      'back the strategy for your approval.',
  },
  {
    id: 'PYTHON',
    label: 'Python Build',
    kicker: 'Full code',
    language: 'PYTHON',
    blurb:
      'Write the logic yourself, with your own signals, data and models. ' +
      'Runs on the same execution and risk stack as every other lane.',
  },
] as const

export type ForgeLaneId = (typeof FORGE_LANES)[number]['id']
